/**
 * React Native mocks for testing environment
 * Provides minimal implementations of React Native components and APIs
 * for cross-platform component testing
 */

import React from 'react';

// Mock StyleSheet with basic functionality
export const StyleSheet = {
  create: <T extends Record<string, unknown>>(styles: T): T => styles,
  compose: (...styles: Array<Record<string, unknown>>): Record<string, unknown> =>
    styles.reduce<Record<string, unknown>>((merged, style) => Object.assign(merged, style), {}),
  flatten: <T>(style: T): T => style,
  hairlineWidth: 1,
  absoluteFillObject: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  absoluteFill: 'absoluteFill',
};

// Mock Dimensions
export const Dimensions = {
  get: (_dimension: 'window' | 'screen') => ({
    width: 375,
    height: 667,
    scale: 2,
    fontScale: 1,
  }),
  addEventListener: () => {},
  removeEventListener: () => {},
};

// Mock Platform - Set to 'web' for web environment
export const Platform = {
  OS: 'web' as const,
  Version: '15.0',
  isPad: false,
  isTesting: true,
  select: <T>(options: { ios?: T; android?: T; native?: T; web?: T; default?: T }): T | undefined =>
    options.web || options.default,
};

// Props accepted by the mock components: standard div props plus the
// React Native-specific props the mock translates into web equivalents.
interface MockComponentProps extends React.ComponentPropsWithoutRef<'div'> {
  testID?: string;
  accessibilityRole?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  accessible?: boolean;
}

// Mock basic React Native components
const createMockComponent = (name: string) => {
  const MockComponent = React.forwardRef<HTMLDivElement, MockComponentProps>((props, ref) => {
    const {
      children,
      testID,
      accessibilityRole,
      accessibilityLabel,
      accessibilityHint,
      accessible: _accessible,
      ...otherProps
    } = props;

    // Convert React Native props to web-compatible props
    const webProps: React.ComponentPropsWithRef<'div'> & { 'data-testid'?: string } = {
      ...otherProps,
      ref,
      'data-testid': testID || name.toLowerCase(),
      className: `rn-${name.toLowerCase()}`,
    };
    
    // Map React Native accessibility props to web accessibility
    if (accessibilityRole) {
      webProps.role = accessibilityRole;
    }
    if (accessibilityLabel) {
      webProps['aria-label'] = accessibilityLabel;
    }
    if (accessibilityHint) {
      webProps['aria-describedby'] = accessibilityHint;
    }
    
    return React.createElement('div', webProps, children);
  });
  MockComponent.displayName = `Mock${name}`;
  return MockComponent;
};

export const View = createMockComponent('View');
export const Text = createMockComponent('Text');
export const TouchableOpacity = createMockComponent('TouchableOpacity');
export const Pressable = createMockComponent('Pressable');
export const Image = createMockComponent('Image');
export const ScrollView = createMockComponent('ScrollView');
export const TextInput = createMockComponent('TextInput');
export const ActivityIndicator = createMockComponent('ActivityIndicator');
export const Modal = createMockComponent('Modal');
export const TouchableWithoutFeedback = createMockComponent('TouchableWithoutFeedback');

// Mock SafeAreaView
export const SafeAreaView = View;

// Mock Alert
export const Alert = {
  alert: () => {},
  prompt: () => {},
};

// Mock Linking
export const Linking = {
  openURL: () => Promise.resolve(),
  canOpenURL: () => Promise.resolve(true),
  getInitialURL: () => Promise.resolve(null),
  addEventListener: () => {},
  removeEventListener: () => {},
};

// Mock DeviceInfo-like functionality
export const DeviceInfo = {
  getModel: () => 'iPhone',
  getSystemVersion: () => '15.0',
};

// Export default mock object with all exports
const ReactNativeMock = {
  StyleSheet,
  Dimensions,
  Platform,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Modal,
  TouchableWithoutFeedback,
  SafeAreaView,
  Alert,
  Linking,
  DeviceInfo,
};

export default ReactNativeMock;